import {
  type CustomFieldData,
  isCustomFieldId,
  type RequirableResidentField,
  type RequiredAccountField,
  type TinyResidentData,
} from '../model'
import { capitalize } from './string'
import { isEmpty, isNotEmpty } from './isEmpty'
import { isRequirableResidentField } from './accountStatus'

export function cleanTinyResidentData(tinyResident: TinyResidentData): TinyResidentData {
  return {
    firstName: tinyResident.firstName,
    lastName: tinyResident.lastName,
    email: tinyResident.email,
    phone: tinyResident.phone,
    phone2: tinyResident.phone2,
    businessName: tinyResident.businessName,
    name: tinyResident.name,
  }
}

export function cleanOptionalTinyResidentData(tinyResident?: TinyResidentData): TinyResidentData | undefined {
  if (!tinyResident) return
  return cleanTinyResidentData(tinyResident)
}

export const residentHasRequiredFields = (
  resident: TinyResidentData,
  requiredFields: RequiredAccountField[] | undefined
): boolean => {
  if (isEmpty(requiredFields)) return true
  const missingFields = getMissingResidentFields(resident, requiredFields)
  return isEmpty(missingFields)
}

export const getMissingResidentFields = (
  resident: TinyResidentData | undefined,
  requiredFields: RequiredAccountField[]
): RequirableResidentField[] => {
  if (isEmpty(requiredFields)) return []
  if (isEmpty(resident)) return requiredFields.filter(isRequirableResidentField)
  return requiredFields.filter((field): field is RequirableResidentField => {
    if (!isRequirableResidentField(field)) return false
    const isValid = isNotEmpty(resident?.[field])
    return !isValid
  })
}

export const stringifyNeedsMoreInfo = (
  needsMoreInfo: RequiredAccountField | 'resident',
  customFields?: CustomFieldData[],
  label = 'customer'
): string => {
  if (isCustomFieldId(needsMoreInfo)) {
    const customField = customFields?.find((field) => field.fieldId === needsMoreInfo)
    return `You must add ${customField?.label ?? needsMoreInfo} before taking this action`
  }

  switch (needsMoreInfo) {
    case 'resident':
      return `You must add ${label} info before taking this action`
    case 'email':
      return `You must add ${label} email before taking this action`
    case 'phone':
      return `You must add ${label} phone number before taking this action`
    case 'firstName':
      return `You must add ${label} first name before taking this action`
    case 'lastName':
      return `You must add ${label} last name before taking this action`
    default:
      return `You must add ${label} info before taking this action`
  }
}

export const getAccountResidentName = (resident: TinyResidentData | undefined): string => {
  if (isNotEmpty(resident?.businessName)) return resident.businessName
  if (isNotEmpty(resident?.firstName) || isNotEmpty(resident?.lastName))
    return `${resident?.firstName ?? ''} ${resident?.lastName ?? ''}`.trim()
  if (isNotEmpty(resident?.name)) return resident.name
  return ''
}

export const normalizeAccountRequiredField = (field: RequiredAccountField): string => {
  if (isCustomFieldId(field)) return field
  switch (field) {
    case 'firstName':
      return 'First Name'
    case 'lastName':
      return 'Last Name'
    case 'phone':
      return 'Phone Number'
    case 'phone2':
      return 'Secondary Phone Number'
    case 'businessName':
      return 'Business Name'
    case 'creditScore':
      return 'Credit Score'
    default:
      return capitalize(field)
  }
}
